import React from "react";
import ReactDOM from "react-dom";

import type {
  ImportProgress,
  ImportResult,
} from "services/dbApi/utilities/exportImport";
import styles from "./Modal.module.scss";

interface ImportProgressOverlayProps {
  progress: ImportProgress;
  /**
   * When provided, the overlay enters its "done" state: hides the progress
   * bar and warning, swaps the header to "Import complete", and shows a
   * Close button.
   */
  result?: ImportResult;
  onClose: () => void;
}

/**
 * Full-screen overlay shown while a long-running import is in flight.
 * Locks the UI, warns the user not to close the tab, and surfaces live
 * counts. Reusable by other long-running flows (e.g. an IDB→DB migration).
 */
export default function ImportProgressOverlay({
  progress,
  result,
  onClose,
}: ImportProgressOverlayProps) {
  const isDone = result !== undefined;

  // Soft guardrail: prompt the user before unload while importing.
  // Modern browsers ignore the returned string but still show their generic
  // "Leave site?" dialog.
  React.useEffect(() => {
    if (isDone) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Calling preventDefault is enough to trigger the browser's generic
      // "Leave site?" prompt. The legacy `returnValue` API is deprecated.
      event.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDone]);

  const completed = progress.imported + progress.skipped + progress.failed;

  const overlay = (
    <div className={styles.overlayBackdrop}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.sectionTitle}>
            {isDone ? "Import complete" : "Importing chats"}
          </h2>
        </div>

        <div className={styles.content}>
          {!isDone && (
            <div className={styles.overlayWarning}>
              Don't close this tab or reload the page until the import is
              finished.
            </div>
          )}

          {!isDone && (
            <progress
              className={styles.overlayProgress}
              max={progress.total || 1}
              value={completed}
            />
          )}

          <p className={styles.overlayStatus}>
            {isDone
              ? "All threads have been processed."
              : progress.currentThreadName
              ? `Importing "${progress.currentThreadName}"…`
              : progress.total === 0
              ? "Reading file…"
              : "Preparing next thread…"}
          </p>

          <div className={styles.overlayCounts}>
            <span>
              <strong>{progress.imported}</strong> imported
            </span>
            <span>
              <strong>{progress.skipped}</strong> skipped
            </span>
            <span>
              <strong>{progress.failed}</strong> failed
            </span>
            <span>
              of <strong>{progress.total}</strong> total
            </span>
          </div>
        </div>

        {isDone && (
          <div className={styles.footer}>
            <button className={styles.button} onClick={onClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(overlay, document.body);
}
